import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Loader2, AlertCircle, UserCheck, ArrowRight, Gift } from "lucide-react";
import axios from "axios";

const API  = process.env.REACT_APP_BACKEND_URL;
const DARK = "#0A0A0A";
const GOLD = "#f59e0b";

export default function ReferralRedirect() {
  const { slug }     = useParams();
  const navigate     = useNavigate();
  const [driver, setDriver]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(false);

  useEffect(() => {
    if (!slug) return;
    axios.get(`${API}/api/market/referral/lookup/${slug}`)
      .then(r => setDriver(r.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [slug]);

  function handleApply() {
    navigate(`/apply?ref=${driver.driver_id}`);
  }

  const fullName = driver ? `${driver.first_name} ${driver.last_name}`.trim() : "";
  const initials = driver
    ? `${(driver.first_name || "?")[0]}${(driver.last_name || "?")[0]}`.toUpperCase()
    : "?";
  const photoUrl = driver?.has_photo
    ? `${API}/api/market/drivers/${driver.driver_id}/photo`
    : null;

  const helmetTitle = driver ? `${fullName} Referred You — Hylas Companion` : "Referral — Hylas Companion";
  const helmetDesc  = driver
    ? `${fullName} referred you to Hylas Companion. Apply now and both of you earn a $400 referral bonus.`
    : "Hylas Companion driver referral";
  const ogImage     = photoUrl || null;
  const ogUrl       = `https://hylascompanion.com/referral/${slug}`;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: DARK }}>
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: "rgba(255,255,255,0.2)" }} />
      </div>
    );
  }

  if (error || !driver) {
    return (
      <>
        <Helmet>
          <title>Referral Not Found — Hylas Companion</title>
        </Helmet>
        <div
          className="min-h-screen flex flex-col items-center justify-center gap-6 px-6"
          style={{ background: DARK }}
        >
          <AlertCircle className="w-12 h-12" style={{ color: "rgba(255,255,255,0.2)" }} />
          <div className="text-center">
            <p
              className="text-xl font-bold uppercase tracking-[0.1em] text-white"
              style={{ fontFamily: "Georgia, serif" }}
            >
              Link Not Found
            </p>
            <p className="text-white/40 text-sm mt-2">
              This referral link may be invalid or expired.
            </p>
          </div>
          <button
            onClick={() => navigate("/apply")}
            className="px-6 py-3 text-sm font-bold uppercase tracking-[0.15em] transition-opacity hover:opacity-80"
            style={{ background: GOLD, color: "#000" }}
          >
            Apply Anyway
          </button>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>{helmetTitle}</title>
        <meta name="description" content={helmetDesc} />
        {/* Open Graph — WhatsApp, iMessage, Slack, etc. */}
        <meta property="og:type"        content="website" />
        <meta property="og:title"       content={helmetTitle} />
        <meta property="og:description" content={helmetDesc} />
        <meta property="og:url"         content={ogUrl} />
        {ogImage && <meta property="og:image" content={ogImage} />}
        {ogImage && <meta property="og:image:width"  content="400" />}
        {ogImage && <meta property="og:image:height" content="400" />}
        <meta property="og:site_name" content="Hylas Companion" />
        {/* Twitter / X card */}
        <meta name="twitter:card"        content={ogImage ? "summary_large_image" : "summary"} />
        <meta name="twitter:title"       content={helmetTitle} />
        <meta name="twitter:description" content={helmetDesc} />
        {ogImage && <meta name="twitter:image" content={ogImage} />}
      </Helmet>

      <div
        className="min-h-screen flex flex-col items-center justify-center px-6 py-16"
        style={{ background: DARK }}
      >
        {/* Grain overlay */}
        <div
          className="fixed inset-0 pointer-events-none"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.04'/%3E%3C/svg%3E\")",
            opacity: 0.5,
          }}
        />

        <div className="relative z-10 w-full max-w-md flex flex-col items-center gap-8">

          {/* Logo */}
          <a
            href="/"
            className="font-bold uppercase tracking-[0.18em] text-lg"
            style={{
              fontFamily: "Georgia, serif",
              color: GOLD,
              letterSpacing: "0.22em",
              textDecoration: "none",
            }}
          >
            HYLAS
          </a>

          {/* Card */}
          <div
            className="w-full rounded-sm p-8 flex flex-col items-center gap-6"
            style={{
              background: "#111",
              border: "1px solid rgba(255,255,255,0.07)",
              borderTop: `3px solid ${GOLD}`,
            }}
          >
            {/* Avatar */}
            <div className="relative">
              <div
                className="w-20 h-20 rounded-full flex items-center justify-center text-2xl font-bold overflow-hidden"
                style={{
                  background: `linear-gradient(135deg, ${GOLD}22, ${GOLD}44)`,
                  border: `2px solid ${GOLD}66`,
                  color: GOLD,
                  fontFamily: "Georgia, serif",
                }}
              >
                {photoUrl ? (
                  <img
                    src={photoUrl}
                    alt={fullName}
                    className="w-full h-full object-cover"
                    onError={e => { e.currentTarget.style.display = "none"; }}
                  />
                ) : (
                  initials
                )}
              </div>
              <div
                className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full flex items-center justify-center"
                style={{ background: "#4ADE80", border: "2px solid #111" }}
              >
                <UserCheck className="w-3 h-3 text-black" />
              </div>
            </div>

            {/* Headline */}
            <div className="text-center">
              <p className="text-white/50 text-xs uppercase tracking-[0.2em] mb-1">
                You were referred by
              </p>
              <p
                className="text-2xl font-bold text-white"
                style={{ fontFamily: "Georgia, serif", letterSpacing: "0.04em" }}
              >
                {fullName}
              </p>
              <p className="text-white/40 text-sm mt-1">
                a Hylas Companion driver
              </p>
            </div>

            {/* Bonus pill */}
            <div
              className="w-full flex items-center gap-3 px-4 py-3 rounded-sm"
              style={{
                background: `${GOLD}11`,
                border: `1px solid ${GOLD}44`,
              }}
            >
              <Gift className="w-4 h-4 flex-shrink-0" style={{ color: GOLD }} />
              <div>
                <p className="text-sm font-bold" style={{ color: GOLD }}>
                  $400 referral bonus — for both of you
                </p>
                <p className="text-white/40 text-xs mt-0.5">
                  Paid after your successful carrier transfer
                </p>
              </div>
            </div>

            {/* CTA */}
            <button
              onClick={handleApply}
              data-testid="referral-apply-btn"
              className="w-full flex items-center justify-center gap-3 py-4 text-sm font-bold uppercase tracking-[0.18em] transition-all hover:opacity-90 active:scale-[0.98]"
              style={{ background: GOLD, color: "#000" }}
            >
              Apply Now
              <ArrowRight className="w-4 h-4" />
            </button>

            <p className="text-white/20 text-xs text-center">
              Free to apply. No commitment required.
            </p>
          </div>

          {/* Footer */}
          <p className="text-white/20 text-xs text-center">
            Hylas Companion Trucking ·{" "}
            <a href="/market" className="underline underline-offset-2 hover:text-white/40">
              Driver Market
            </a>
          </p>
        </div>
      </div>
    </>
  );
}
